import {
  WhmAvatar,
  WhmBox,
  WhmLink,
  WhmSkeleton,
  WhmTypography,
  whmTheme,
  useMediaQuery,
  Theme,
  WhmStack,
} from "@securitize/reactjs-whm";
import { ReactNode, useMemo } from "react";
import { useGetPromotions } from "../../../data/usePortfolio";
import { ReactComponent as ArrowLineRight } from "@securitize/assets-sec-ui/dist/icons/svg/arrow-line-right.svg";
import { Translate } from "../../Translation/Translate";
import { TranslationKey } from "../../../contexts/I18nContext/translationKeys";
import StyledPortfolioCard from "./StyledCard";
import { isValidHttpUrl } from "../../../utils/url-utils";

const PromotionLink = ({
  link,
  linkText,
}: {
  link: string;
  linkText: ReactNode;
}) => {
  return (
    <WhmLink
      href={link}
      color="primary"
      underline="hover"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <WhmBox sx={{ display: "flex", alignItems: "center" }}>
        {linkText}
        <WhmBox
          component={ArrowLineRight}
          sx={{
            color: whmTheme.palette.primary.main,
            fill: whmTheme.palette.primary.main,
            width: "20px",
            height: "20px",
            marginLeft: "8px",
          }}
        />
      </WhmBox>
    </WhmLink>
  );
};

export default function PortfolioPromotionCards() {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("xl"),
  );
  const promotionsQuery = useGetPromotions();

  const promotions = useMemo(
    () => (promotionsQuery.data ?? []).filter((p) => p.isActive),
    [promotionsQuery.data],
  );

  if (promotionsQuery.isLoading) {
    return (
      <StyledPortfolioCard
        sx={{
          gap: "16px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <WhmStack gap="16px">
          <WhmBox sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <WhmSkeleton variant="circular" width={48} height={48} />
            <WhmSkeleton variant="rounded" sx={{ flex: 1 }} height={36} />
            {!isMobile && (
              <WhmSkeleton variant="rounded" width={220} height={36} />
            )}
          </WhmBox>
          <WhmSkeleton variant="rounded" height={36} />
          {isMobile && (
            <WhmSkeleton variant="rounded" width={220} height={36} />
          )}
        </WhmStack>
      </StyledPortfolioCard>
    );
  }

  if (!promotions.length) return null;

  return (
    <WhmBox>
      {promotions.map(({ id, imageLink, title, link, linkText, subtitle }) => (
        <StyledPortfolioCard
          key={id}
          sx={{
            gap: "16px",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={() => {
            window.open(link, "_blank");
          }}
        >
          <WhmStack gap="16px">
            <WhmBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <WhmBox
                sx={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {isValidHttpUrl(imageLink) && (
                  <WhmAvatar
                    src={imageLink}
                    sx={{
                      width: "32px",
                      height: "32px",
                    }}
                  />
                )}
                <WhmTypography variant="subtitle1">
                  <Translate translationKey={title as TranslationKey} />
                </WhmTypography>
              </WhmBox>
              {!isMobile && (
                <PromotionLink
                  link={link}
                  linkText={
                    <Translate translationKey={linkText as TranslationKey} />
                  }
                />
              )}
            </WhmBox>
            {subtitle && (
              <WhmTypography variant="body1">
                <Translate translationKey={subtitle as TranslationKey} />
              </WhmTypography>
            )}
            {isMobile && (
              <PromotionLink
                link={link}
                linkText={
                  <Translate translationKey={linkText as TranslationKey} />
                }
              />
            )}
          </WhmStack>
        </StyledPortfolioCard>
      ))}
    </WhmBox>
  );
}
